import { useTranslation } from '@thoughtspot/i18n';
import cx from 'classnames';
import React, { useState } from 'react';
import { Button } from '../../../button';
import { Icon, IconColor, IconID } from '../../../icon';
import { Horizontal } from '../../../layout';
import styles from '../pagination-dots.module.scss';
import { FETCH_NEXT_PAGES_WHEN_REMAINING_PAGES_ARE, getPaginationDots, hoverPaginationDotIconID } from '../pagination-dots.util';
/**
 * The PaginationOptimizedDots component will retrieve the necessary list of objects initally which are required to show the pagination dots accurately.
 * It doesn't require fetching the total count of objects, thereby avoiding any potential performance implications.
 *
 * In order to display five pagination dots,
 * we need to initially fetch five sets of pages and determine if more pages exist based on the isLastBatch flag.
 *
 * When the initial fetch indicates that isLastBatch is true,
 * then the subsequent fetches for the next and previous pages are not required,
 * as all the objects are already available in the cache.
 *
 * When the isLastBatch is false and clicking the next button,
 * we don't need to fetch objects for the first three pages; we can display them from the object cache.
 * However, for the fourth page, it's necessary to fetch the next set of objects to accurately represent the pagination dots for additional pages.
 *
 * When the isLastBatch is false, and clicking the previous button,
 * we don't need to fetch objects for the last three pages; we can display them from the object cache.
 * However, for the second page, it's necessary to fetch the previous set of objects to accurately represent the pagination dots for previous pages.
 */
export const PaginationOptimizedDots = ({ offset, batchSize, previous, next, jumpTo, fetch, }) => {
    const { t } = useTranslation();
    let totalObjectsFetched = fetch.offset + fetch.objectsCount;
    if (fetch.isLastBatch === false)
        totalObjectsFetched += 1;
    const pages = Math.ceil(totalObjectsFetched / batchSize);
    const currentPage = Math.floor(offset / batchSize) + 1;
    const paginationDotProp = getPaginationDots(pages, currentPage);
    // Do not show the pagination dots for less than two pages..
    if (offset === 0 && totalObjectsFetched <= batchSize) {
        return React.createElement("div", { "data-testid": "no-pagination-testid" });
    }
    /**
     * To retrieve the previous set of objects, the fetch offset must not be zero,
     * and both the fetch offset and pagination offset should be equal.
     *
     * In essence, since we can ascertain the preceding objects using the fetch offset value,
     * the necessity to fetch previous objects arises only when there are no cached objects available for that particular page.
     */
    const previousClicked = () => {
        previous();
        if (fetch.offset > 0 && fetch.offset === offset) {
            fetch.previous();
        }
    };
    const getPreviousButton = () => {
        return (React.createElement(Button, { ariaLabel: t('PREVIOUS'), "data-testid": "PaginationLeftButton", className: cx(styles.paginationLeft, {
                [styles.enabled]: paginationDotProp.disablePrevious === false,
            }), onClick: previousClicked, isDisabled: paginationDotProp.disablePrevious, icon: IconID.CHEVRON_LEFT, iconClassName: styles.paginationIcon, type: "tertiary", size: "s", retainFocusOnClick: false }));
    };
    /**
     * For instance, during the initial fetch, if we obtain 50 objects and the isLastBatch flag indicates false for a batch size of 10,
     * we can denote the existence of additional pages by displaying medium and small dots at the 4th and 5th positions, respectively.
     * Subsequently, when we are on the 3rd page and click on the next button,
     * we need to fetch the next set of objects to ascertain whether there are any additional objects beyond the current batch.
     *
     * In summary, when we click next for every 4th page, it will be necessary to fetch a new set of objects starting from that offset.
     */
    const getNextClicked = () => {
        next();
        if (fetch.isLastBatch === true)
            return;
        if (pages <= currentPage + FETCH_NEXT_PAGES_WHEN_REMAINING_PAGES_ARE) {
            fetch.next();
        }
    };
    const getNextButton = () => {
        return (React.createElement(Button, { ariaLabel: t('NEXT'), "data-testid": "PaginationRightButton", className: cx(styles.paginationRight, {
                [styles.enabled]: paginationDotProp.disableNext === false,
            }), onClick: getNextClicked, isDisabled: paginationDotProp.disableNext, icon: IconID.CHEVRON_RIGHT, iconClassName: styles.paginationIcon, type: "tertiary", size: "s", retainFocusOnClick: false }));
    };
    /**
     * jumpToHandler method is used to jump to the specific page index.
     * @param pageIndex index of the page
     */
    const jumpToHandler = (pageIndex) => {
        jumpTo(pageIndex);
        // previous pages
        if (pageIndex + 1 < currentPage) {
            if (fetch.offset === 0)
                return;
            if (pageIndex * batchSize < fetch.offset) {
                fetch.previous(fetch.offset);
            }
            return;
        }
        // next pages
        if (pages <= pageIndex + FETCH_NEXT_PAGES_WHEN_REMAINING_PAGES_ARE) {
            if (fetch.isLastBatch === true)
                return;
            fetch.next(pages - FETCH_NEXT_PAGES_WHEN_REMAINING_PAGES_ARE);
        }
    };
    const PaginationDot = ({ icon, size, pageIndex, }) => {
        const [isMouseHover, setIsMouseHover] = useState(false);
        // Selected pagination dot will not have hover effect and clickability.
        if (icon === IconID.CIRCLE) {
            return (React.createElement(Horizontal, { className: styles.dotContainer, "data-testid": `pagination-dot-${pageIndex}` },
                React.createElement(Icon, { id: icon, color: IconColor.GRAY, size: size, className: styles.dot })));
        }
        return (React.createElement(Horizontal, { className: styles.cursorPointer, onClick: () => jumpToHandler(pageIndex), onMouseOver: () => setIsMouseHover(true), onMouseOut: () => setIsMouseHover(false), "data-testid": `pagination-dot-${pageIndex}` },
            React.createElement(Icon, { id: isMouseHover ? hoverPaginationDotIconID : icon, color: IconColor.GRAY, size: size, className: styles.dot })));
    };
    return (React.createElement(Horizontal, { vAlignContent: "center", hAlignContent: "center", className: styles.pagination },
        getPreviousButton(),
        paginationDotProp.dots.map((paginationDot) => (React.createElement(PaginationDot, { ...paginationDot, key: paginationDot.key }))),
        getNextButton()));
};
//# sourceMappingURL=pagination-optimized-dots.js.map